import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import { Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import CodigoVerificacionModal from '../components/CodigoVerificacionModal';
import ResetPasswordWizard from '../components/ResetPasswordWizard';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

function Login() {
    const [activeTab, setActiveTab] = useState('signin');
    const [nombre, setNombre] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [mostrarModal, setMostrarModal] = useState(false);
    const [mostrarResetPasswordModal, setMostrarResetPasswordModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [emailRegistrado, setEmailRegistrado] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailValid, setEmailValid] = useState(null);
    const [passwordsMatch, setPasswordsMatch] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, user } = useAuth();

    // Leer return URL del query param — solo rutas relativas (seguridad: evita open redirect)
    const rawReturn = new URLSearchParams(location.search).get('return') || '';
    const returnUrl = rawReturn.startsWith('/') ? rawReturn : '/home';

    const [passwordCriteria, setPasswordCriteria] = useState({
        length: false, upper: false, lower: false, number: false, symbol: false
    });

    // Fix para Safari iOS - Manejo de viewport height dinámico
    useEffect(() => {
        const setVH = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };
        
        setVH();
        window.addEventListener('resize', setVH);
        window.addEventListener('orientationchange', setVH);
        
        return () => {
            window.removeEventListener('resize', setVH);
            window.removeEventListener('orientationchange', setVH);
        };
    }, []);

    useEffect(() => {
        const criteria = {
            length: password.length >= 9,
            upper: /[A-Z]/.test(password),
            lower: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            symbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>\/?]/.test(password)
        };
        setPasswordCriteria(criteria);
    }, [password]);

    // 🎯 UX MEJORADO: Solo mostrar validaciones cuando password NO es válido
    const allPasswordCriteriaPassed = Object.values(passwordCriteria).every(v => v);
    const showPasswordRequirements = password.length > 0 && !allPasswordCriteriaPassed;

    // Debug temporal
    console.log('🔍 DEBUG Login:', {
        passwordLength: password.length,
        criteria: passwordCriteria,
        allPassed: allPasswordCriteriaPassed,
        showRequirements: showPasswordRequirements,
        activeTab
    });

    // Validación en tiempo real del email
    useEffect(() => {
        if (email) {
            setEmailValid(validateEmail(email));
        } else {
            setEmailValid(null);
        }
    }, [email]);

    // Validación en tiempo real de coincidencia de contraseñas
    useEffect(() => {
        if (activeTab === 'signup' && confirmPassword) {
            setPasswordsMatch(password === confirmPassword);
        } else {
            setPasswordsMatch(null);
        }
    }, [password, confirmPassword, activeTab]);

    // Limpiar estados al cambiar tab
    useEffect(() => {
        setError('');
        setSuccessMessage('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setNombre('');
        setEmailValid(null);
        setPasswordsMatch(null);
    }, [activeTab]);

    useEffect(() => {
        // Solo navegar cuando tanto isAuthenticated como user están disponibles
        if (isAuthenticated && user) {
            console.log('✅ Login - Usuario autenticado, redirigiendo a:', returnUrl);
            navigate(returnUrl, { replace: true });
        }
    }, [isAuthenticated, user, navigate]);

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        setError('');
    };

    const validateEmail = (email) => /\S+@\S+\.\S+/.test(email);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            if (activeTab === 'signin') {
                if (!email || !password) {
                    setError('Por favor, ingresa tu email y contraseña.');
                    return;
                }
                if (!validateEmail(email)) {
                    setError('Por favor, ingresa un email válido.');
                    return;
                }

                const response = await apiClient.post('/login', { email, password });
                login(response.data.user, response.data.token);
            } else {
                if (!nombre || !email || !password || !confirmPassword) {
                    setError('Por favor, completa todos los campos.');
                    return;
                }
                if (password !== confirmPassword) {
                    setError('Las contraseñas no coinciden.');
                    return;
                }
                if (!Object.values(passwordCriteria).every(Boolean)) {
                    setError('La contraseña no cumple con todos los criterios.');
                    return;
                }

                await apiClient.post('/registro', { nombre, email, password });
                setEmailRegistrado(email);
                setMostrarModal(true);
                setError('');
            }
        } catch (err) {
            console.error('Error:', err.response ? err.response.data : err.message);
            
            // Manejar errores específicos
            const errorCode = err.response?.data?.error;
            const errorMessage = err.response?.data?.message;
            
            let displayError;
            if (errorCode === 'USER_INACTIVE') {
                displayError = errorMessage || 'Tu cuenta ha sido desactivada. Contacta al administrador.';
            } else if (errorCode === 'EMAIL_NOT_VERIFIED') {
                displayError = 'Por favor, verifica tu email antes de iniciar sesión.';
            } else if (errorCode === 'INVALID_PASSWORD') {
                displayError = 'Contraseña incorrecta.';
            } else if (errorCode === 'USER_NOT_FOUND') {
                displayError = 'Usuario no encontrado.';
            } else {
                displayError = errorCode || 
                    (activeTab === 'signin' ? 'Error al iniciar sesión. Verifica tus credenciales.' : 'Error al registrarse. Inténtalo de nuevo.');
            }
            
            setError(displayError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerificationSuccess = async () => {
        setIsSubmitting(true);
        try {
            const response = await apiClient.post('/login', { email: emailRegistrado, password });
            login(response.data.user, response.data.token);
            setMostrarModal(false);
        } catch (err) {
            console.error('Falló el login automático post-verificación:', err);
            setMostrarModal(false);
            setError('Verificación exitosa. Por favor, inicia sesión manualmente.');
            setActiveTab('signin');
        } finally {
            setIsSubmitting(false);
        }
    };

    // J410772: Handler de éxito del reset de contraseña
    const handleResetPasswordSuccess = (message) => {
        setActiveTab('signin');
        setSuccessMessage(message);
        setEmail('');
        setPassword('');
        
        // Limpiar mensaje después de 10 segundos
        setTimeout(() => {
            setSuccessMessage('');
        }, 10000);
    };

    return (
        <>
            <SEOHead 
                title="Iniciar Sesión | Crear Cuenta - Edutech Pulse Lab"
                description="Accede a simuladores IA, laboratorios de innovación y entrenamiento ejecutivo. Crea tu cuenta gratis o inicia sesión para comenzar tu aprendizaje."
                robots="noindex, follow"
            />
            <Navbar />
            <div className="min-h-[calc(100vh-var(--header-height-mobile))] md:min-h-[calc(100vh-var(--header-height-desktop))] supports-[min-height:100dvh]:min-h-[calc(100dvh-var(--header-height-mobile))] md:supports-[min-height:100dvh]:min-h-[calc(100dvh-var(--header-height-desktop))] bg-gradient-to-b from-[#f9fafb] to-white flex flex-col pt-4 md:pt-8">
                {/* Formulario de Login/Registro */}
                <section className="flex-1 bg-gray-50 flex items-center justify-center px-3 sm:px-4 py-6 sm:py-8">
                    <div className="w-full max-w-md mx-auto">
                        <div className="bg-white p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl border border-gray-100">
                            <div className="flex justify-center mb-6 sm:mb-8 gap-2 sm:gap-3">
                                <button
                                    onClick={() => setActiveTab('signin')}
                                    disabled={isSubmitting}
                                    className={`flex-1 sm:flex-initial px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold rounded-full transition-all duration-300 ${
                                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                    } ${activeTab === 'signin' ? 'bg-[#0c203b] text-white shadow-md' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
                                >
                                    Ingresar
                                </button>
                                <button
                                    onClick={() => setActiveTab('signup')}
                                    disabled={isSubmitting}
                                    className={`flex-1 sm:flex-initial px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold rounded-full transition-all duration-300 ${
                                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                    } ${activeTab === 'signup' ? 'bg-[#0c203b] text-white shadow-md' : 'text-gray-700 bg-gray-100 hover:bg-gray-200'}`}
                                >
                                    Registrarse
                                </button>
                            </div>

                            {/* J410772: Mensaje de éxito después de resetear contraseña */}
                            {successMessage && (
                                <div className="bg-green-50 border border-green-200 text-green-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl mb-4 sm:mb-6 text-xs sm:text-sm flex items-start gap-2" role="alert">
                                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span className="leading-relaxed">{successMessage}</span>
                                </div>
                            )}

                            <h2 className="text-lg sm:text-xl font-bold text-center text-gray-900 mb-5 sm:mb-6 px-2">
                                {activeTab === 'signin' ? 'Bienvenido de nuevo' : 'Crea tu cuenta'}
                            </h2>

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl mb-4 sm:mb-6 text-xs sm:text-sm flex items-start gap-2" role="alert">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span className="leading-relaxed">{error}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 text-sm">
                                {activeTab === 'signup' && (
                                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                        <input 
                                            type="text" 
                                            placeholder="Nombre completo" 
                                            value={nombre} 
                                            onChange={(e) => setNombre(e.target.value)} 
                                            className="w-full px-3 sm:px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl text-gray-900 focus:ring-2 focus:ring-[#0c203b] focus:border-[#0c203b] text-sm sm:text-base placeholder-gray-500 transition-all" 
                                            required 
                                            disabled={isSubmitting}
                                        />
                                        <div className="relative">
                                            <input 
                                                type="email" 
                                                placeholder="Correo electrónico" 
                                                value={email} 
                                                onChange={(e) => setEmail(e.target.value)} 
                                                className={`w-full px-3 sm:px-4 py-3 sm:py-4 border-2 rounded-lg sm:rounded-xl text-gray-900 focus:ring-2 text-sm sm:text-base placeholder-gray-500 transition-all pr-10 sm:pr-12 ${
                                                    emailValid === null ? 'border-gray-200 focus:ring-[#0c203b] focus:border-[#0c203b]' :
                                                    emailValid ? 'border-green-300 focus:ring-green-500 focus:border-green-500' :
                                                    'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                }`}
                                                required 
                                                disabled={isSubmitting}
                                            />
                                            {emailValid !== null && (
                                                <div className="absolute inset-y-0 right-3 flex items-center">
                                                    {emailValid ? (
                                                        <span className="text-green-500 text-base sm:text-lg font-semibold">✓</span>
                                                    ) : (
                                                        <span className="text-red-500 text-base sm:text-lg font-semibold">✕</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'signin' && (
                                    <div className="relative">
                                        <input 
                                            type="email" 
                                            placeholder="Correo electrónico" 
                                            value={email} 
                                            onChange={(e) => setEmail(e.target.value)} 
                                            className={`w-full px-3 sm:px-4 py-3 sm:py-4 border-2 rounded-lg sm:rounded-xl text-gray-900 focus:ring-2 text-sm sm:text-base placeholder-gray-500 transition-all pr-10 sm:pr-12 ${
                                                emailValid === null ? 'border-gray-200 focus:ring-[#0c203b] focus:border-[#0c203b]' :
                                                emailValid ? 'border-green-300 focus:ring-green-500 focus:border-green-500' :
                                                'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            }`}
                                            required 
                                            disabled={isSubmitting}
                                        />
                                        {emailValid !== null && (
                                            <div className="absolute inset-y-0 right-3 flex items-center">
                                                {emailValid ? (
                                                    <span className="text-green-500 text-base sm:text-lg font-semibold">✓</span>
                                                ) : (
                                                    <span className="text-red-500 text-base sm:text-lg font-semibold">✕</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}


                                <div className="relative">
                                    <input 
                                        type={showPassword ? 'text' : 'password'} 
                                        placeholder="Contraseña" 
                                        value={password} 
                                        onChange={handlePasswordChange} 
                                        className="w-full px-3 sm:px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl text-gray-900 focus:ring-2 focus:ring-[#0c203b] focus:border-[#0c203b] pr-10 sm:pr-12 text-sm sm:text-base placeholder-gray-500 transition-all" 
                                        required 
                                        disabled={isSubmitting}
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowPassword(!showPassword)} 
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700 active:text-gray-900 transition-colors"
                                        disabled={isSubmitting}
                                    >
                                        {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                                    </button>
                                </div>

                                {/* J410772: Link "¿Olvidaste tu contraseña?" - Solo en tab signin, DESPUÉS del campo contraseña */}
                                {activeTab === 'signin' && (
                                    <div className="text-right -mt-2">
                                        <button
                                            type="button"
                                            onClick={() => setMostrarResetPasswordModal(true)}
                                            className="text-xs sm:text-sm text-[#0c203b] hover:text-[#1a365d] font-medium transition-colors"
                                            disabled={isSubmitting}
                                        >
                                            ¿Olvidaste tu contraseña?
                                        </button>
                                    </div>
                                )}

                                {/* 🎯 VALIDACIONES: Solo se muestran cuando password NO es válido */}
                                {activeTab === 'signup' && showPasswordRequirements && (
                                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg sm:rounded-xl p-3 sm:p-4 text-xs sm:text-sm text-gray-600 space-y-1.5 sm:space-y-2 border border-gray-200">
                                        <p className="font-semibold text-gray-800 mb-2 text-xs sm:text-sm">Requisitos de contraseña:</p>
                                        <div className="grid grid-cols-1 gap-1.5">
                                            <p className={`flex items-center gap-2 ${passwordCriteria.length ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                                <span className="text-base">{passwordCriteria.length ? '✓' : '○'}</span>
                                                <span>Mínimo 9 caracteres</span>
                                            </p>
                                            <p className={`flex items-center gap-2 ${passwordCriteria.upper ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                                <span className="text-base">{passwordCriteria.upper ? '✓' : '○'}</span>
                                                <span>Una mayúscula</span>
                                            </p>
                                            <p className={`flex items-center gap-2 ${passwordCriteria.lower ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                                <span className="text-base">{passwordCriteria.lower ? '✓' : '○'}</span>
                                                <span>Una minúscula</span>
                                            </p>
                                            <p className={`flex items-center gap-2 ${passwordCriteria.number ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                                <span className="text-base">{passwordCriteria.number ? '✓' : '○'}</span>
                                                <span>Un número</span>
                                            </p>
                                            <p className={`flex items-center gap-2 ${passwordCriteria.symbol ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                                                <span className="text-base">{passwordCriteria.symbol ? '✓' : '○'}</span>
                                                <span>Un símbolo (!@#$%^&*)</span>
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* 🎯 CONFIRMAR PASSWORD: Solo aparece cuando password es válido */}
                                {activeTab === 'signup' && allPasswordCriteriaPassed && (
                                    <div className="relative">
                                        <input 
                                            type={showConfirmPassword ? 'text' : 'password'} 
                                            placeholder="Confirmar contraseña" 
                                            value={confirmPassword} 
                                            onChange={(e) => setConfirmPassword(e.target.value)} 
                                            className={`w-full px-3 sm:px-4 py-3 sm:py-4 border-2 rounded-lg sm:rounded-xl text-gray-900 focus:ring-2 pr-16 sm:pr-20 text-sm sm:text-base placeholder-gray-500 transition-all ${
                                                passwordsMatch === null ? 'border-gray-200 focus:ring-[#0c203b] focus:border-[#0c203b]' :
                                                passwordsMatch ? 'border-green-300 focus:ring-green-500 focus:border-green-500' :
                                                'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            }`}
                                            required 
                                            disabled={isSubmitting}
                                        />
                                        <div className="absolute inset-y-0 right-3 flex items-center space-x-1.5 sm:space-x-2">
                                            {passwordsMatch !== null && (
                                                <span className={`text-base sm:text-lg font-semibold ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}>
                                                    {passwordsMatch ? '✓' : '✕'}
                                                </span>
                                            )}
                                            <button 
                                                type="button" 
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                                                className="text-gray-500 hover:text-gray-700 active:text-gray-900 transition-colors"
                                                disabled={isSubmitting}
                                            >
                                                {showConfirmPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Mensaje visual para contraseñas no coincidentes */}
                                {activeTab === 'signup' && passwordsMatch === false && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                        <span className="text-red-700 text-xs sm:text-sm font-medium">Las contraseñas no coinciden</span>
                                    </div>
                                )}

                                <button 
                                    type="submit" 
                                    disabled={isSubmitting || (activeTab === 'signup' && (!emailValid || passwordsMatch === false || !Object.values(passwordCriteria).every(Boolean)))}
                                    className={`w-full py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base mt-6 sm:mt-8 shadow-lg transition-all duration-300 flex items-center justify-center ${
                                        isSubmitting || (activeTab === 'signup' && (!emailValid || passwordsMatch === false || !Object.values(passwordCriteria).every(Boolean)))
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                            : 'bg-gradient-to-r from-[#0c203b] to-[#1a365d] text-white hover:from-[#1a365d] hover:to-[#0c203b] active:scale-[0.98] hover:shadow-xl'
                                    }`}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>{activeTab === 'signin' ? 'Ingresando...' : 'Registrando...'}</span>
                                        </>
                                    ) : (
                                        activeTab === 'signin' ? 'Ingresar' : 'Registrarme'
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </section>

                {/* Footer alineado con Home */}
                <footer className="bg-[#0a1929] py-3 sm:py-4 md:py-8 border-t border-gray-800 mt-auto">

                {/* J410772: Modal de Recuperación de Contraseña */}
                {mostrarResetPasswordModal && (
                    <ResetPasswordWizard
                        onClose={() => setMostrarResetPasswordModal(false)}
                        onSuccess={handleResetPasswordSuccess}
                    />
                )}
                    <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
                        <p className="text-gray-400 text-[10px] sm:text-xs md:text-sm leading-relaxed">
                            © 2025 <span className="font-semibold text-white">Edutech Pulse Lab™</span> 
                            <span className="hidden sm:inline"> · Donde la inteligencia artificial entrena la innovación humana</span>
                        </p>
                    </div>
                </footer>

                {/* Modal de Verificación */}
                {mostrarModal && (
                    <>
                        <div className="fixed inset-0 bg-black bg-opacity-50 z-40"></div>
                        <div className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
                            <div className="w-full sm:max-w-md">
                                <CodigoVerificacionModal
                                    email={emailRegistrado}
                                    onSuccess={handleVerificationSuccess}
                                    onClose={() => setMostrarModal(false)}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default Login;